import React, { useEffect, useState } from 'react';
import { BsArrowLeftShort, BsArrowRightShort } from 'react-icons/bs';
import FormSelect from 'components/FormFields/FormSelect';
import { useHistory } from 'react-router-dom';

import { saveOnBoardData, finishBoarding } from 'api/onBoarding';
import APFormGroup from 'components/FormFields/APFormGroup';
import FormItem from 'components/FormFields/FormItem';
import {
  CURRENCIES,
  DATE_FORMATS,
  TIME_FORMATS,
  SURVERY_OPTIONS,
  SURVERY_EVENTS_OPTIONS,
  SURVEY_PLAN_TO_USE_OPTIONS,
  SURVEY_YEARS_IN_BUSINESS_OPTIONS,
} from 'utils/constants';
import { getPutFormat } from './filter';
import './styles.scss';

const Step5 = ({ formData }) => {
  const history = useHistory();

  const [isSubmitted, setFormSubmitted] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [dateFormat, setDateFormat] = useState('mm/dd/yyyy');
  const [timeFormat, setTimeFormat] = useState('12hour');
  const [surveyOption, setSurveyOption] = useState(null);
  const [surveyEventsOption, setSurveyEventsOption] = useState(null);
  const [surveyPlanToUseOption, setSurveyPlanToUseOption] = useState(null);
  const [surveyYearsInBusinessOption, setSurveyYearsInBusinessOption] = useState(null);

  const isValid = [surveyOption, surveyEventsOption, surveyPlanToUseOption, surveyYearsInBusinessOption].every(Boolean);

  useEffect(() => {
    const { preferences } = formData;
    if (preferences) {
      setCurrency(preferences.currency || 'USD');
      setDateFormat(preferences.dateFormat?.toLowerCase() || 'mm/dd/yyyy');
      setTimeFormat(preferences.timeFormat || '12hour');
      setSurveyOption(preferences.hear_about_us || null);
      setSurveyEventsOption(preferences.number_of_events || null);
      setSurveyPlanToUseOption(preferences.plan_to_use || null);
      setSurveyYearsInBusinessOption(preferences.years_in_business || null);
    }
  }, [formData]);

  const saveForms = async () => {
    const newFormData = formData;
    newFormData.preferences.currency = currency;
    newFormData.preferences.dateFormat = dateFormat;
    newFormData.preferences.timeFormat = timeFormat;
    newFormData.preferences.hear_about_us = surveyOption;
    newFormData.preferences.number_of_events = surveyEventsOption;
    newFormData.preferences.plan_to_use = surveyPlanToUseOption;
    newFormData.preferences.years_in_business = surveyYearsInBusinessOption;

    return saveOnBoardData({ boarding: getPutFormat(newFormData) });
  };

  const handleBack = () => {
    history.push('/app/v2/account-setup/step/5');
  };

  const handleNext = async () => {
    setFormSubmitted(true);
    if (isValid) {
      const isSaved = await saveForms();
      if (isSaved) {
        const isSuccess = await finishBoarding();
        if (isSuccess)
          window.location.href = `/app/pro/${formData.companyInfo.orgId}/welcome`;
      }
    }
  };

  return (
    <>
      <section className="stepflow_content">
        <article className="stepflow_form">
          <section className="stepflow_form-header">
            <h3 className="form-title">Account preferences</h3>
            <p className="form-subtitle text-center"
              style={{ width: '100%' }}
            >
              Let’s set your default preferences. You can always change these in your business settings,
              and adjust your preferences for each project.
            </p>
          </section>
          <APFormGroup
            gridGap={1}
          >
            <FormItem
              id="currency"
              name="Currency"
            >
              <FormSelect
                id="currency"
                name=""
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                showSearch
                required={false}
                labelHidden
              >
                {CURRENCIES.map((option, index) => (
                  <option
                    value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </FormSelect>
            </FormItem>
            <FormItem
              id="date-format"
              name="Date format"
            >
              <FormSelect
                id="date-format"
                name=""
                value={dateFormat}
                onChange={(e) => setDateFormat(e.target.value)}
                required={false}
                labelHidden
              >
                {DATE_FORMATS.map((option, index) => (
                  <option
                    value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </FormSelect>
            </FormItem>
            <FormItem
              id="time-format"
              name="Time Format"
            >
              <FormSelect
                id="time-format"
                name=""
                value={timeFormat}
                onChange={(e) => setTimeFormat(e.target.value)}
                required={false}
                labelHidden
              >
                {TIME_FORMATS.map((option, index) => (
                  <option
                    value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </FormSelect>
            </FormItem>
          </APFormGroup>
        </article>
        <article className="stepflow_form stepflow-last-question">
          <section className="stepflow_form-header">
            <h3 className="form-title">A few more questions</h3>
          </section>
          <APFormGroup
            verticalGap={0}
          >
            <FormItem
              id="how-heard"
              name="How did you first hear about us?"
              className="form-item--w-50"
              labelProps={{
                className: 'no-wrap',
              }}
              required
            >
              <FormSelect
                id="survey-option"
                name=""
                value={surveyOption}
                onChange={(e) => {
                  setFormSubmitted(false);
                  setSurveyOption(e.target.value);
                }}
                labelHidden
                showError={isSubmitted && !surveyOption}
              >
                {SURVERY_OPTIONS.map((option, index) => (
                  <option
                    value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </FormSelect>
            </FormItem>
            <FormItem
              id="events-planned"
              name="Average # of events planned per year?"
              className="form-item--w-50"
              labelProps={{
                className: 'no-wrap',
              }}
              required
            >
              <FormSelect
                id="events-planned"
                name=""
                value={surveyEventsOption}
                onChange={(e) => {
                  setFormSubmitted(false);
                  setSurveyEventsOption(e.target.value);
                }}
                labelHidden
                showError={isSubmitted && !surveyEventsOption}
              >
                {SURVERY_EVENTS_OPTIONS.map((option, index) => (
                  <option
                    value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </FormSelect>
            </FormItem>
          </APFormGroup>
          <APFormGroup
            verticalGap={0}
          >
            <FormItem
              id="plan-to-use"
              name="How do you plan to use Aisle Planner Pro?"
              className="form-item--w-50"
              labelProps={{
                className: 'no-wrap',
              }}
              required
            >
              <FormSelect
                id="plan-to-use"
                name=""
                value={surveyPlanToUseOption}
                onChange={(e) => {
                  setFormSubmitted(false);
                  setSurveyPlanToUseOption(e.target.value);
                }}
                labelHidden
                showError={isSubmitted && !surveyPlanToUseOption}
              >
                {SURVEY_PLAN_TO_USE_OPTIONS.map((option, index) => (
                  <option
                    value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </FormSelect>
            </FormItem>
            <FormItem
              id="years-in-business"
              name="How many years in business?"
              className="form-item--w-50"
              labelProps={{
                className: 'no-wrap',
              }}
              required
            >
              <FormSelect
                id="years-in-business"
                name=""
                value={surveyYearsInBusinessOption}
                onChange={(e) => {
                  setFormSubmitted(false);
                  setSurveyYearsInBusinessOption(e.target.value);
                }}
                labelHidden
                showError={isSubmitted && !surveyYearsInBusinessOption}
              >
                {SURVEY_YEARS_IN_BUSINESS_OPTIONS.map((option, index) => (
                  <option
                    value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </FormSelect>
            </FormItem>
          </APFormGroup>
        </article>
      </section>
      <section className="stepflow_footer">
        <button className="stepflow_footer-button"
          onClick={handleBack}
        >
          <BsArrowLeftShort size={20} /> Back
        </button>
        {isSubmitted && !isValid && (
          <div className="stepflow_footer-error">
            Please complete required fields above.
          </div>
        )}
        <button
          className="stepflow_footer-button"
          color="primary"
          onClick={handleNext}
        >
          Finish
          <BsArrowRightShort size={20} />
        </button>
      </section>
    </>
  );
};

export default Step5;
